import {
  Fragment, 
  ReactNode
} from "react";
import {
  EventDetailsForView,
  EventPictureType,
  EventWithCustomField,
  ParticipationRequestType,
} from "@ac-expo/api-types";
import { FileType } from "@prisma/client";
import { Button } from "../../Button";
import { Separator } from "../../Separator";
import {
  InformationPanelDetailsSection,
  InformationPanelEntrancesSection,
  InformationPanelPicturesSection,
  InformationPanelScheduleSection,
  InformationPanelSocialSection,
} from "./InformationPanelSections";

type InformationPanelProps = {
  as: "organiser" | "exhibitor" | "visitor";
  event: EventWithCustomField<EventDetailsForView>;
  participationRequestStatus: ParticipationRequestType["status"] | null;
  isTicketingVisible: boolean;
  pictures: EventPictureType[];
  getFileUrl: (key: string, type: FileType) => string;
  /** Replaces the default schedule section, keeping the order the organiser configured. */
  scheduleSection?: ReactNode;
  onSocialsClick?: () => void;
  onWebsiteClick?: () => void;
  onConversationClick?: () => void;
  onOpenTicketing?: () => void;
  /** Group unlock line shown under the ticketing button on small screens. */
  groupUnlockLine?: ReactNode;
  onRequestParticipation?: () => void;
  actionGuard?: (
    action: () => void,
    fallback?: () => void,
    ignoreModal?: boolean
  ) => void;
};

export function InformationPanel({
  as,
  event,
  participationRequestStatus,
  isTicketingVisible,
  pictures,
  getFileUrl,
  scheduleSection,
  onSocialsClick,
  onWebsiteClick,
  onConversationClick,
  onOpenTicketing,
  groupUnlockLine,
  onRequestParticipation,
  actionGuard,
}: InformationPanelProps) {
  const sections = event.customEventAppearance?.eventInfoSections ?? [
    "mainCharacteristics",
    "locations",
    "dates",
    "pictures",
    "links",
  ];

  return (
    <div className="gap-6 px-6 py-8">
      <div className="gap-4">
        {sections.map((section, index) => {
          let content: ReactNode = null;
          switch (section) {
            case "mainCharacteristics":
              content = (
                <InformationPanelDetailsSection
                  key={index}
                  as={as}
                  event={event}
                  participationRequestStatus={participationRequestStatus}
                  isTicketingVisible={isTicketingVisible}
                  onWebsiteClick={onWebsiteClick}
                  onConversationClick={onConversationClick}
                  onRequestParticipation={onRequestParticipation}
                  actionGuard={actionGuard}
                />
              );
              break;
            case "dates":
              content = scheduleSection ?? (
                <InformationPanelScheduleSection key={index} event={event} />
              );
              break;
            case "locations":
              content = (
                <InformationPanelEntrancesSection key={index} event={event} />
              );
              break;
            case "pictures":
              content = (
                <InformationPanelPicturesSection
                  key={index}
                  pictures={pictures}
                  getFileUrl={getFileUrl}
                />
              );
              break;
            case "links":
              content = (
                <InformationPanelSocialSection
                  key={index}
                  event={event}
                  onSocialsClick={onSocialsClick}
                />
              );
              break;
          }

          if (content === null) return null;

          return (
            <Fragment key={index}>
              {index !== 0 && <Separator />}
              {content}
            </Fragment>
          );
        })}
        {isTicketingVisible && event.isTicketingAvailable && (
          <>
            <Button
              onClick={onOpenTicketing}
              variant="blue"
              className="mt-6 w-full px-4 font-semibold"
            >
              Accéder à la billetterie
            </Button>
            {groupUnlockLine && (
              <div className="mt-3 flex justify-center">{groupUnlockLine}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default InformationPanel;
